'use client';

import React, { useState, useEffect } from 'react';

import Link from 'next/link';

import { clearSearchQuery } from '@hub/common/src/search-utils';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import CatalogItIcon from '@hub/common-components/src/SVG/CatalogItIcon';

export default function Navbar({
  priorPageURL,
  disableSearch = false,
  disableNavigation = false,
  allowBackNavigation = true
}: {
  priorPageURL?: string;
  disableSearch?: boolean;
  disableNavigation?: boolean;
  allowBackNavigation?: boolean;
}) {
  const searchParams = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [prevPageURL, setPrevPageURL] = useState(priorPageURL);

  const router = useRouter();
  const pathname = usePathname();

  // Set search query after component mounts to avoid hydration mismatch
  useEffect(() => {
    // eslint-disable-next-line
    setSearchQuery(searchParams.get('query') || '');
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    if (typeof window === 'undefined') {
      return;
    }

    const input = e.currentTarget.querySelector('input');
    if (input) {
      input.blur();
    }
    if (!searchQuery.trim()) {
      const newUrl = new URL(window.location.origin + pathname + window.location.search);
      newUrl.searchParams.delete('query');
      // Preserve testTheme when clearing search
      if (newUrl.pathname.includes('/entry/')) {
        // can't use router here because it causes page navigation and is intercepted by @modal route.
        // a null state makes next sync its router to the new url without a fetch; with its own state
        // kept it would not, and its next update would put the query back
        window.history.replaceState(null, '', newUrl.pathname + newUrl.search);
      } else {
        router.push(newUrl.pathname + newUrl.search);
      }
      return;
    }

    // Define routes that should redirect to /search
    const searchRedirectRoutes = ['/', '/classifications', '/search'];

    // Get current pathname without query parameters
    const currentPathname = pathname.split('?')[0];

    if (searchRedirectRoutes.includes(currentPathname) || currentPathname.includes('/entry/')) {
      // Redirect to /search with query parameter
      const searchParams = new URLSearchParams();
      searchParams.set('query', searchQuery);
      router.push(`/search?${searchParams.toString()}`);
    } else {
      // Keep existing behavior for other routes
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('query', searchQuery);

      // Keep any existing search parameters except 'query'
      const existingParams = new URLSearchParams(window.location.search);
      existingParams.forEach((value, key) => {
        if (key !== 'query') {
          currentUrl.searchParams.set(key, value);
        }
      });

      router.push(currentUrl.pathname + currentUrl.search);
    }
  };

  const handleClearSearch = () =>
    clearSearchQuery(router, () => {
      setSearchQuery('');
      if (prevPageURL) {
        // remove query param from prior page url, preserve testTheme
        const prevUrl = new URL(window.location.origin + prevPageURL);
        prevUrl.searchParams.delete('query');
        setPrevPageURL(prevUrl.pathname + (prevUrl.search ? prevUrl.search : ''));
      }
    });

  return (
    <nav className='navbar fixed top-0 left-0 right-0 bg-gray-800 text-white p-4 w-full z-50'>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-2 md:space-x-4 flex-1'>
          <div className='flex items-center shrink-0 md:shrink'>
            {prevPageURL ? (
              <Link
                href={prevPageURL}
                className='text-white flex hover:text-gray-300'
                shallow={true}
                prefetch={false}
                style={{
                  pointerEvents: disableNavigation && !allowBackNavigation ? 'none' : 'auto'
                }}
              >
                <button className='text-2xl cursor-pointer'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='w-6 h-6 cursor-pointer'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
                  </svg>
                </button>
              </Link>
            ) : (
              <div className='min-w-6'>
                <span className='block md:hidden text-white text-[20px] font-bold flex items-center'>
                  HUB
                </span>
              </div>
            )}
          </div>
          <Link
            href='/'
            className='flex items-center space-x-2 shrink-0 hidden md:flex'
            prefetch={false}
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
          >
            <CatalogItIcon size={40} />
            <span className='text-white text-[28px] font-bold flex items-center'>HUB</span>
          </Link>
          <form
            id='search-bar'
            action='#'
            onSubmit={handleSearch}
            className='flex items-center flex-1 md:flex-none min-w-0 mx-2 md:mx-0'
            suppressHydrationWarning
          >
            <div className='search flex relative items-center bg-gray-700 rounded-[25px]'>
              {/* Show logo in search bar and hide search icon on mobile screens */}

              {/* Logo */}
              <CatalogItIcon size={30} className='ml-3 md:hidden' />
              {/* Search Icon */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                opacity={disableSearch ? 0.2 : 1}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='w-5 h-5 text-gray-400 ml-3 hidden md:block'
                viewBox='0 0 24 24'
              >
                <circle cx='11' cy='11' r='8' />
                <line x1='21' y1='21' x2='16.65' y2='16.65' />
              </svg>

              <input
                name='search'
                enterKeyHint='search'
                type='search'
                placeholder='Search...'
                value={searchQuery}
                disabled={disableSearch}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-2 py-2 bg-gray-700 text-white focus:outline-none rounded-[25px] w-full disabled:opacity-20 disabled:cursor-not-allowed'
                suppressHydrationWarning
              />
              {searchQuery && (
                <button
                  type='button'
                  onClick={handleClearSearch}
                  className='absolute right-3 text-gray-400 hover:text-white cursor-pointer'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='w-5 h-5'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Mobile menu button */}
        <button className='md:hidden' onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
            stroke='currentColor'
            className='w-6 h-6'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z'
            />
          </svg>
        </button>

        {/* Desktop menu items - hidden on mobile */}
        <div
          className='hidden md:flex items-center space-x-6'
          style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
        >
          <Link href='/collections' className='hover:text-gray-300' prefetch={false}>
            Collections
          </Link>
          <Link href='/classifications' className='hover:text-gray-300' prefetch={false}>
            Classifications
          </Link>
          <Link href='/about' className='hover:text-gray-300' prefetch={false}>
            About
          </Link>
          <Link href='https://www.catalogit.app/' className='hover:text-gray-300' prefetch={false}>
            App
          </Link>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {isMenuOpen && (
        <div className='absolute md:hidden top-full right-0 mt-2 mr-2 w-48 bg-gray-800 rounded-lg shadow-lg py-2 z-50'>
          <Link
            href='/'
            className='block px-4 py-2 text-white hover:bg-gray-700'
            prefetch={false}
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
          >
            <div className='flex justify-start gap-6'>
              <CatalogItIcon size={24} color='#fff' /> <p>Home</p>
            </div>
          </Link>
          <Link
            href='/collections'
            className='block px-4 py-2 text-white hover:bg-gray-700'
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
            prefetch={false}
          >
            <div className='flex justify-start items-center gap-6'>
              {/* Building Icon */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                height='24'
                width='24'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M2 5l10-4 10 4v2H2V5z' />
                <rect x='4' y='8' width='4' height='8' />
                <rect x='10' y='8' width='4' height='8' />
                <rect x='16' y='8' width='4' height='8' />
                <rect x='2' y='18' width='20' height='3' />
              </svg>

              <p>Collections</p>
            </div>
          </Link>
          <Link
            href='/classifications'
            className='block px-4 py-2 text-white hover:bg-gray-700'
            prefetch={false}
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
          >
            <div className='flex justify-start items-center gap-6'>
              {/* Folder Icon */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                height='24'
                width='24'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path d='M2 6h8l2 2h10v2H2V6z' />
                <path d='M2 10h20v10H2V10z' />
              </svg>

              <p>Classifications</p>
            </div>
          </Link>
          <Link
            href='/about'
            className='block px-4 py-2 text-white hover:bg-gray-700'
            prefetch={false}
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
          >
            <div className='flex justify-start items-center gap-6'>
              {/* Book Icon */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='28'
                viewBox='0 0 24 28'
                role='img'
                aria-label='Taller open book with cutout lines'
              >
                <defs>
                  <mask id='book-mask'>
                    <rect width='24' height='28' fill='white' />
                    <line x1='12' y1='4' x2='12' y2='24' stroke='black' strokeWidth='1.4' />
                    <line x1='4.5' y1='9' x2='9.5' y2='9' stroke='black' strokeWidth='1.2' />
                    <line x1='4.5' y1='13' x2='9.5' y2='13' stroke='black' strokeWidth='1.2' />
                    <line x1='4.5' y1='17' x2='9.5' y2='17' stroke='black' strokeWidth='1.2' />
                    <line x1='4.5' y1='21' x2='9.5' y2='21' stroke='black' strokeWidth='1.2' />
                    <line x1='14.5' y1='9' x2='19.5' y2='9' stroke='black' strokeWidth='1.2' />
                    <line x1='14.5' y1='13' x2='19.5' y2='13' stroke='black' strokeWidth='1.2' />
                    <line x1='14.5' y1='17' x2='19.5' y2='17' stroke='black' strokeWidth='1.2' />
                    <line x1='14.5' y1='21' x2='19.5' y2='21' stroke='black' strokeWidth='1.2' />
                  </mask>
                </defs>

                <path
                  d='M2 4h7c2 0 3 1 3 3v16c0-1.5-1-3-3-3H2z
           M22 4h-7c-2 0-3 1-3 3v16c0-1.5 1-3 3-3h7z'
                  fill='currentColor'
                  mask='url(#book-mask)'
                />
              </svg>

              <p>About</p>
            </div>
          </Link>
          <Link
            href='https://www.catalogit.app/'
            className='block px-4 py-2 text-white hover:bg-gray-700'
            prefetch={false}
            style={{ pointerEvents: disableNavigation ? 'none' : 'auto' }}
          >
            <div className='flex justify-start items-center gap-6'>
              {/* Info Icon */}
              <svg
                xmlns='http://www.w3.org/2000/svg'
                height='24'
                width='24'
                viewBox='0 0 24 24'
                fill='currentColor'
              >
                <path
                  d='
                      M12 2
                      C6.48 2 2 6.48 2 12
                      C2 17.52 6.48 22 12 22
                      C17.52 22 22 17.52 22 12
                      C22 6.48 17.52 2 12 2
                      Z
                      M11 10
                      H13
                      V17
                      H11
                      V10
                      Z
                      M11 7
                      H13
                      V9
                      H11
                      V7
                      Z
                    '
                />
              </svg>

              <p>App</p>
            </div>
          </Link>
        </div>
      )}
    </nav>
  );
}
